import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import { api } from "@/api"
import { ENDPOINTS } from "@/constants"
import { toast } from "@/hooks/use-toast"
import type {
  CustomerCard,
  CustomerCardsResponse,
} from "@/types/customer-wallet"

// Mensagem amigável a partir de um erro desconhecido (sem `any`) — mesmo formato
// usado nos services do painel admin.
const getErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message ===
      "string"
  ) {
    return (error as { message: string }).message
  }
  return fallback
}

const cardsUrl = (customerId: string) =>
  ENDPOINTS.CUSTOMER_CARDS.replace(
    "{{customerId}}",
    customerId
  )

const cardUrl = (customerId: string, cardId: string) =>
  ENDPOINTS.CUSTOMER_CARD_BY_ID.replace(
    "{{customerId}}",
    customerId
  ).replace("{{cardId}}", cardId)

export const customerCardsQueryKey = (customerId: string) =>
  [ENDPOINTS.CUSTOMER_CARDS, customerId] as const

export const getCustomerCards = async (
  customerId: string
): Promise<CustomerCard[]> => {
  const { data } = await api.get<
    CustomerCardsResponse | CustomerCard[]
  >(cardsUrl(customerId))
  // O payment devolve um envelope ({ cards: [...] }) porque lista nua vazia vira
  // 204 SEM CORPO no gateway — e o axios entrega corpo ausente como string vazia,
  // que não é nullish. Aceita as duas formas para não depender da ordem de deploy.
  if (Array.isArray(data)) return data
  return Array.isArray(data?.cards) ? data.cards : []
}

/** Cartões salvos na carteira do cliente (aba Carteira — só admin). */
export const useCustomerCards = (
  customerId: string,
  enabled = true
) =>
  useQuery({
    queryKey: customerCardsQueryKey(customerId),
    queryFn: () => getCustomerCards(customerId),
    enabled: enabled && Boolean(customerId),
  })

/**
 * Exclui um cartão da carteira.
 *
 * Sem otimismo de propósito: a exclusão é irreversível pela tela e o payment pode
 * recusá-la (regra do cartão principal). Mostrar a linha sumindo antes da resposta
 * daria a entender que deu certo quando ainda pode falhar.
 */
export const useDeleteCustomerCardMutation = (
  customerId: string
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (cardId: string) => {
      const { data } = await api.delete(
        cardUrl(customerId, cardId)
      )
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: customerCardsQueryKey(customerId),
      })
    },
    onError: (error: unknown) => {
      toast({
        title: getErrorMessage(
          error,
          "Erro ao excluir o cartão"
        ),
        variant: "destructive",
      })
    },
  })
}
